import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';

import {
    DEFAULT_EBAY_COUNTRY,
    DEFAULT_ITEM_LOCATION,
    EBAY_COUNTRIES,
    getExpectedCurrency,
    isEbayCountryCode,
    isItemLocationCode,
    itemLocationOptions,
    type EbayCountryCode,
    type ItemLocationCode,
} from '../../data/countries';
import { DEMO_KEYWORDS, type DemoKeyword } from '../../data/demo';
import { ACCOUNT_QUERY_KEY } from '../../hooks/accountQuery';
import { useAccount } from '../../hooks/useAccount';
import { ApiError, api } from '../../lib/api';
import { formatDateTime, formatMoney, timeAgo } from '../../lib/format';
import {
    CONSOLE_COUNT_MAX,
    COUNT_DEFAULT,
    COUNT_MIN,
    PUBLIC_COUNT_MAX,
    countFieldState,
    formatV1SearchCurl,
    formatV1SearchJs,
    normalizeCountInput,
    resolveCount,
    type SampleSearchForm,
} from '../../lib/sample';
import type { ConsoleSearchListing, ConsoleSearchRequest, ConsoleSearchResult } from '../../types';
import { CodeBlock } from './CodeBlock';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Note } from '../ui/Note';
import { Panel } from '../ui/Panel';

/** Slice size for paging through the already-fetched payload (not an API page). */
const ITEM_PAGE_SIZE = 10;

const SELECT_CLASS =
    'w-full rounded-md border border-border-strong bg-surface px-3.5 py-2.5 text-base text-text outline-none focus:border-secondary';

const SORT_OPTIONS = [
    { value: 'newly_listed', label: 'Newly listed' },
    { value: 'best_match', label: 'Best match' },
    { value: 'ending_soonest', label: 'Ending soonest' },
    { value: 'price_low', label: 'Price: low' },
    { value: 'price_high', label: 'Price: high' },
] as const;

type SortValue = (typeof SORT_OPTIONS)[number]['value'];

const LISTING_TYPE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'auction', label: 'Auction' },
    { value: 'buy_it_now', label: 'Buy It Now' },
] as const;

type ListingTypeValue = (typeof LISTING_TYPE_OPTIONS)[number]['value'];

export type SearchPlaygroundMode = 'demo' | 'compact' | 'full';

type SearchPlaygroundProps = {
    /**
     * `demo`, public landing chips → cached `/api/demo/search` (no auth).
     * `compact`, dashboard teaser (core fields, count fixed at 60).
     * `full`, docs page (all public query knobs; try still capped at 60).
     */
    mode?: SearchPlaygroundMode;
};

type FormState = SampleSearchForm & {
    sort: SortValue;
    listingType: ListingTypeValue;
    country: EbayCountryCode;
    itemLocation: ItemLocationCode;
};

function defaultForm(): FormState {
    return {
        keyword: 'iphone 15 pro',
        sold: true,
        country: DEFAULT_EBAY_COUNTRY,
        itemLocation: DEFAULT_ITEM_LOCATION,
        page: '1',
        count: String(COUNT_DEFAULT),
        daysToScrape: '0',
        sort: 'newly_listed',
        condition: '',
        listingType: 'all',
        minPrice: '',
        maxPrice: '',
    };
}

function Field({
    label,
    hint,
    children,
    className = '',
}: {
    label: string;
    hint?: string;
    children: ReactNode;
    className?: string;
}) {
    return (
        <label className={`block min-w-0 ${className}`}>
            <span className="eyebrow mb-2 block text-text-muted">{label}</span>
            {children}
            {hint ? <span className="mt-1.5 block text-xs text-text-muted">{hint}</span> : null}
        </label>
    );
}

function itemPrice(item: ConsoleSearchListing, poolCurrency: string): string {
    const low = item.soldPrice ?? item.price ?? null;
    const high = item.soldPriceMax ?? item.priceMax ?? null;
    const base = formatMoney(low, item.currency, poolCurrency);
    if (low != null && high != null && high !== low) {
        return `${base}–${formatMoney(high, item.currency, poolCurrency)}`;
    }
    return base;
}

function itemShipping(item: ConsoleSearchListing, poolCurrency: string): string {
    if (item.shippingType === 'free') return 'Free';
    if (item.shipping == null) return '-';
    return formatMoney(item.shipping, item.currency, poolCurrency);
}

function parseOptionalNumber(raw: string): number | undefined {
    const t = raw.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
}

/**
 * Demo rows carry the grade plus every aspect eBay listed, joined with middots:
 * "Pre-Owned · Apple iPhone 15 Pro · 256 GB · AT&T, Verizon, T-Mobile, Sprint".
 * The grade is the only part a visitor is reading. The rest is real data and
 * stays in the API response; it just does not belong in a marketing table.
 */
function conditionGrade(raw: string | null | undefined): string {
    if (!raw) return '-';
    return raw.split('·')[0]!.trim() || '-';
}

function ResultTable({
    result,
    formCountry,
    formCurrency,
    pageItems,
    pageStart,
    itemCount,
    totalItemPages,
    safeItemPage,
    setItemPage,
    plain = false,
}: {
    result: ConsoleSearchResult;
    formCountry: EbayCountryCode;
    formCurrency: string;
    pageItems: ConsoleSearchListing[];
    pageStart: number;
    itemCount: number;
    totalItemPages: number;
    safeItemPage: number;
    setItemPage: (fn: (p: number) => number) => void;
    /**
     * Landing page. Drops the API vocabulary: no cardsOnPage/totalItems split,
     * no page cursor, no cache flag, no row numbers. A visitor deciding whether
     * the data is real does not need to know what we called the fields, and
     * "200 returned of 60 matching · more available via page=" reads as a bug
     * even when it isn't. The console and docs keep the full line, because
     * there the counts are the thing being debugged.
     */
    plain?: boolean;
}) {
    const resultCurrency = result.currency ?? formCurrency;
    const country = result.country ?? formCountry;
    const countryName = EBAY_COUNTRIES.find((c) => c.code === country)?.name ?? country;
    const pulledRelative = timeAgo(result.fetchedAt);
    const pulledAbsolute = formatDateTime(result.fetchedAt);

    return (
        <div className="mb-5 border border-border bg-bg-sunk p-4">
            {plain ? (
                <div className="space-y-1">
                    <p className="text-sm text-text">
                        {itemCount} completed {itemCount === 1 ? 'sale' : 'sales'} on eBay{' '}
                        {countryName}, priced in {resultCurrency}.
                    </p>
                    <p className="text-sm text-text-muted">
                        Data pulled from eBay{' '}
                        <span className="font-medium text-text">{pulledRelative}</span>
                        {pulledAbsolute !== '-' ? (
                            <span className="font-mono text-xs"> · {pulledAbsolute}</span>
                        ) : null}
                    </p>
                </div>
            ) : (
                <p className="text-sm text-text">
                    <span className="font-medium">{result.cardsOnPage}</span> returned
                    {result.totalItems > 0 ? (
                        <>
                            {' '}
                            of <span className="font-medium">{result.totalItems}</span> matching
                        </>
                    ) : null}{' '}
                    · API page {result.page}
                    {result.hasNextPage ? ' · more available via page=' : ''}
                    {result.sold ? ' · sold' : ' · active'}
                    {' · '}
                    {country}
                    {result.itemLocation && result.itemLocation !== 'worldwide'
                        ? ` · ${itemLocationOptions(country).find((o) => o.code === result.itemLocation)?.label ?? result.itemLocation}`
                        : ''}
                    {' · '}
                    {resultCurrency}
                    {result.cached ? ' · cached' : ''}
                    {result.fetchedAt
                        ? ` · pulled ${pulledRelative}`
                        : ''}
                </p>
            )}

            {itemCount === 0 ? (
                <p className="mt-3 text-sm text-text-muted">No items in this response.</p>
            ) : (
                <>
                    <div className="mt-3 overflow-x-auto border border-border bg-surface">
                        <table className="w-full min-w-2xl border-collapse text-left text-sm">
                            <thead>
                                <tr className="border-b border-border text-xs text-text-muted">
                                    {plain ? null : <th className="px-3 py-2 font-medium">#</th>}
                                    <th className="px-3 py-2 font-medium">Item</th>
                                    <th className="px-3 py-2 font-medium">{plain ? 'Sold for' : 'Price'}</th>
                                    <th className="px-3 py-2 font-medium">{plain ? 'Shipping' : 'Ship'}</th>
                                    <th className="px-3 py-2 font-medium">{plain ? 'Sold on' : 'Sold'}</th>
                                    <th className="px-3 py-2 font-medium">Condition</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((item, i) => {
                                    const n = pageStart + i + 1;
                                    const key = item.itemId ?? `${pageStart + i}`;
                                    return (
                                        <tr key={key} className="border-b border-border last:border-b-0">
                                            {plain ? null : (
                                                <td className="px-3 py-2 font-mono text-xs text-text-muted">{n}</td>
                                            )}
                                            <td className="max-w-sm px-3 py-2 text-text">
                                                {item.url ? (
                                                    /*
                                                      Body text, not primary. Sixty listing titles
                                                      painted in the primary colour would drown the
                                                      actual CTA — primary is for actions only.
                                                    */
                                                    <a
                                                        href={item.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="line-clamp-2 text-text underline decoration-border-strong underline-offset-2 hover:decoration-text"
                                                    >
                                                        {item.title}
                                                    </a>
                                                ) : (
                                                    <span className="line-clamp-2">{item.title}</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                                                {itemPrice(item, resultCurrency)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-text-muted">
                                                {itemShipping(item, resultCurrency)}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-text-muted">
                                                {item.soldDate ?? '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-text-muted">
                                                {plain ? conditionGrade(item.condition) : (item.condition ?? '-')}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalItemPages > 1 ? (
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                            <p className="font-mono text-xs text-text-muted">
                                {pageStart + 1}–{Math.min(pageStart + ITEM_PAGE_SIZE, itemCount)} of {itemCount}
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    disabled={safeItemPage <= 1}
                                    onClick={() => setItemPage((p) => Math.max(1, p - 1))}
                                >
                                    Prev
                                </Button>
                                <span className="font-mono text-xs text-text-muted">
                                    {safeItemPage}/{totalItemPages}
                                </span>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    disabled={safeItemPage >= totalItemPages}
                                    onClick={() => setItemPage((p) => Math.min(totalItemPages, p + 1))}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <p className="mt-3 font-mono text-xs text-text-muted">
                            {itemCount} item{itemCount === 1 ? '' : 's'}
                        </p>
                    )}
                </>
            )}
        </div>
    );
}

/**
 * Live try + copy-paste samples for GET /v1/search.
 *
 * Run uses the session cookie via POST /api/console/search, no key paste.
 * That still burns monthly quota. Snippets show the real Bearer /v1 path.
 *
 * Demo mode is marketing only: fixed keywords, cached hourly, no credentials.
 */
export function SearchPlayground({ mode = 'compact' }: SearchPlaygroundProps) {
    const client = useQueryClient();
    const account = useAccount();
    const signedIn = Boolean(account.data);
    const isDemo = mode === 'demo';
    const isFull = mode === 'full';

    const [form, setForm] = useState<FormState>(defaultForm);
    const [demoKeyword, setDemoKeyword] = useState<DemoKeyword>(DEMO_KEYWORDS[0]);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ConsoleSearchResult | null>(null);
    /** 1-based page into `result.items` only, never refetches. */
    const [itemPage, setItemPage] = useState(1);

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://actualsold.com';
    const poolCurrency = getExpectedCurrency(form.country);
    const sellerOptions = itemLocationOptions(form.country);

    // Free typing in the field; limits apply here only.
    // Snippet = public /v1 (≤240). Try = session console (≤60).
    const countUi = countFieldState(form.count, PUBLIC_COUNT_MAX);
    const snippetCount = isFull ? countUi.resolved : CONSOLE_COUNT_MAX;
    const tryCount = resolveCount(form.count, CONSOLE_COUNT_MAX);
    const tryClampedFromPublic = isFull && countUi.resolved > CONSOLE_COUNT_MAX;
    const snippetOpts = { explicit: isFull } as const;
    const curlSample = formatV1SearchCurl(origin, form, snippetCount, snippetOpts);
    const jsSample = formatV1SearchJs(origin, form, snippetCount, snippetOpts);

    const itemCount = result?.items.length ?? 0;
    const totalItemPages = Math.max(1, Math.ceil(itemCount / ITEM_PAGE_SIZE));
    const safeItemPage = Math.min(itemPage, totalItemPages);
    const pageStart = (safeItemPage - 1) * ITEM_PAGE_SIZE;
    const pageItems = result?.items.slice(pageStart, pageStart + ITEM_PAGE_SIZE) ?? [];

    function patch(partial: Partial<FormState>) {
        setForm((prev) => ({ ...prev, ...partial }));
    }

    useEffect(() => {
        if (!isDemo) return;
        let cancelled = false;
        async function load() {
            setPending(true);
            setError(null);
            setItemPage(1);
            try {
                const bodyResult = await api.demoSearch(demoKeyword);
                if (!cancelled) {
                    setResult(bodyResult);
                    setItemPage(1);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof ApiError ? err.message : 'Something went wrong.');
                }
            } finally {
                if (!cancelled) setPending(false);
            }
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, [isDemo, demoKeyword]);

    async function runSearch() {
        const trimmedKeyword = form.keyword.trim();
        if (!trimmedKeyword || pending || !signedIn || isDemo) return;

        // Normalize count in the field so the form matches what we send.
        const normalizedCount = isFull
            ? normalizeCountInput(form.count, PUBLIC_COUNT_MAX)
            : String(CONSOLE_COUNT_MAX);
        if (isFull && normalizedCount !== form.count) {
            patch({ count: normalizedCount });
        }
        const runCount = isFull
            ? resolveCount(normalizedCount, CONSOLE_COUNT_MAX)
            : CONSOLE_COUNT_MAX;

        setPending(true);
        setError(null);
        setResult(null);
        setItemPage(1);

        const body: ConsoleSearchRequest = {
            keyword: trimmedKeyword,
            sold: form.sold,
            country: form.country,
            itemLocation: form.itemLocation,
            count: runCount,
        };

        if (isFull) {
            body.page = Math.min(100, Math.max(1, parseInt(form.page, 10) || 1));
            body.daysToScrape = Math.min(365, Math.max(0, parseInt(form.daysToScrape, 10) || 0));
            body.sort = form.sort;
            body.listingType = form.listingType;
            const condition = form.condition.trim();
            if (condition) body.condition = condition;
            const minPrice = parseOptionalNumber(form.minPrice);
            const maxPrice = parseOptionalNumber(form.maxPrice);
            if (minPrice !== undefined) body.minPrice = minPrice;
            if (maxPrice !== undefined) body.maxPrice = maxPrice;
        }

        try {
            const bodyResult = await api.consoleSearch(body);
            setResult(bodyResult);
            setItemPage(1);
            void client.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Something went wrong.');
        } finally {
            setPending(false);
        }
    }

    if (isDemo) {
        // No "demo · cached" badge on the panel. It answered a question nobody
        // had arrived with, and it invited the wrong one: whether the product
        // serves stale data. It does not. Only this sample does, and the line
        // under the table says so in words. Pull time comes from fetchedAt.
        return (
            <Panel title="See real sold prices">
                <p className="mb-4 text-sm text-text-muted">
                    Pick a keyword to see what these actually sold for. Every row is a real
                    completed eBay sale.
                </p>

                <div className="mb-5 flex flex-wrap gap-2">
                    {DEMO_KEYWORDS.map((keyword) => {
                        const active = keyword === demoKeyword;
                        return (
                            <button
                                key={keyword}
                                type="button"
                                disabled={pending}
                                onClick={() => setDemoKeyword(keyword)}
                                className={[
                                    'border px-3 py-1.5 text-sm transition-colors',
                                    active
                                        ? 'border-secondary bg-secondary text-bg'
                                        : 'border-border-strong bg-surface text-text hover:border-secondary',
                                    pending ? 'opacity-60' : '',
                                ].join(' ')}
                            >
                                {keyword}
                            </button>
                        );
                    })}
                </div>

                {error ? (
                    <div className="mb-5">
                        <Note tone="warn">{error}</Note>
                    </div>
                ) : null}

                {pending && !result ? (
                    <p className="mb-5 text-sm text-text-muted">Loading sold prices…</p>
                ) : null}

                {result ? (
                    <ResultTable
                        plain
                        result={result}
                        formCountry={form.country}
                        formCurrency={poolCurrency}
                        pageItems={pageItems}
                        pageStart={pageStart}
                        itemCount={itemCount}
                        totalItemPages={totalItemPages}
                        safeItemPage={safeItemPage}
                        setItemPage={setItemPage}
                    />
                ) : null}

                <p className="text-sm text-text-muted">
                    These five keywords refresh on a schedule; the stamp above is when this
                    snapshot was last pulled. Your own searches hit eBay at request time and can
                    be anything.{' '}
                    {/*
                      Body text, not primary. The landing page already has a real
                      CTA button above this panel; a primary link here would
                      compete for the same click.
                    */}
                    <Link
                        to="/signin"
                        className="text-text underline decoration-border-strong underline-offset-2 hover:decoration-text"
                    >
                        Get a free key.
                    </Link>
                </p>
            </Panel>
        );
    }

    return (
        <Panel title="Try it" aside={isFull ? <span className="text-success">live · signed-in</span> : 'curl'}>
            <p className="mb-4 text-sm text-text-muted">
                {signedIn ? (
                    <>
                        Run uses your signed-in session, so there is no key to paste. One request is one page of listings
                        and one billable call
                        {isFull
                            ? ` (snippets follow /v1 count 1–${PUBLIC_COUNT_MAX}; try is limited to ${CONSOLE_COUNT_MAX})`
                            : ''}
                        . Issue a key and call <code className="font-mono text-xs">/v1</code> as in the
                        samples below.
                    </>
                ) : (
                    <>
                        Sign in to run a live request against your quota. Snippets below update with
                        every form control. That is the real{' '}
                        <code className="font-mono text-xs">/v1/search</code> call.
                    </>
                )}
            </p>

            <form
                className="mb-5 space-y-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    void runSearch();
                }}
            >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="sm:col-span-2 lg:col-span-3">
                        <Input
                            label="Keyword"
                            name="keyword"
                            value={form.keyword}
                            onChange={(keyword) => patch({ keyword })}
                            placeholder="iphone 15 pro"
                        />
                    </div>

                    {isFull ? (
                        <Field label="Sold">
                            <select
                                name="sold"
                                value={form.sold ? 'true' : 'false'}
                                onChange={(event) => patch({ sold: event.target.value === 'true' })}
                                className={SELECT_CLASS}
                            >
                                <option value="true">Sold</option>
                                <option value="false">Active</option>
                            </select>
                        </Field>
                    ) : null}

                    <Field label="Marketplace">
                        <select
                            name="country"
                            value={form.country}
                            onChange={(event) => {
                                const next = event.target.value;
                                if (isEbayCountryCode(next)) patch({ country: next });
                            }}
                            className={SELECT_CLASS}
                        >
                            {EBAY_COUNTRIES.map((opt) => (
                                <option key={opt.code} value={opt.code}>
                                    {opt.code} · {opt.name} ({opt.currency})
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Seller location">
                        <select
                            name="itemLocation"
                            value={form.itemLocation}
                            onChange={(event) => {
                                const next = event.target.value;
                                if (isItemLocationCode(next)) patch({ itemLocation: next });
                            }}
                            className={SELECT_CLASS}
                        >
                            {sellerOptions.map((opt) => (
                                <option key={opt.code} value={opt.code}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <div className="flex items-end pb-2.5">
                        <div>
                            <p className="eyebrow mb-2 text-text-muted">Currency</p>
                            <p className="font-mono text-sm font-medium text-text">{poolCurrency}</p>
                        </div>
                    </div>
                </div>

                {isFull ? (
                    <div className="grid grid-cols-2 gap-3 border-t border-border pt-4 sm:grid-cols-3 lg:grid-cols-4">
                        <Input
                            label="Page"
                            name="page"
                            value={form.page}
                            onChange={(page) => patch({ page })}
                            type="number"
                        />
                        <Input
                            label="Count"
                            name="count"
                            value={form.count}
                            onChange={(count) => patch({ count })}
                            type="number"
                            min={COUNT_MIN}
                            max={PUBLIC_COUNT_MAX}
                            step={1}
                            invalid={countUi.invalid}
                            hint={
                                tryClampedFromPublic
                                    ? `${countUi.hint} · try will use ${tryCount}`
                                    : countUi.hint +
                                      (countUi.invalid ? '' : ` · try ≤${CONSOLE_COUNT_MAX}`)
                            }
                            onBlur={() => {
                                patch({ count: normalizeCountInput(form.count, PUBLIC_COUNT_MAX) });
                            }}
                        />
                        <Input
                            label="Days"
                            name="daysToScrape"
                            value={form.daysToScrape}
                            onChange={(daysToScrape) => patch({ daysToScrape })}
                            type="number"
                            hint="0 = no sold-date filter"
                        />
                        <Field label="Sort">
                            <select
                                name="sort"
                                value={form.sort}
                                onChange={(event) => patch({ sort: event.target.value as SortValue })}
                                className={SELECT_CLASS}
                            >
                                {SORT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Format">
                            <select
                                name="listingType"
                                value={form.listingType}
                                onChange={(event) =>
                                    patch({ listingType: event.target.value as ListingTypeValue })
                                }
                                className={SELECT_CLASS}
                            >
                                {LISTING_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <div className="col-span-2 sm:col-span-2 lg:col-span-2">
                            <Input
                                label="Condition"
                                name="condition"
                                value={form.condition}
                                onChange={(condition) => patch({ condition })}
                                placeholder="used,for_parts"
                                hint="Comma-separated; blank = any"
                            />
                        </div>
                        <Input
                            label="Min price"
                            name="minPrice"
                            value={form.minPrice}
                            onChange={(minPrice) => patch({ minPrice })}
                            type="number"
                        />
                        <Input
                            label="Max price"
                            name="maxPrice"
                            value={form.maxPrice}
                            onChange={(maxPrice) => patch({ maxPrice })}
                            type="number"
                        />
                    </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
                    {signedIn ? (
                        <Button type="submit" size="sm" disabled={pending || !form.keyword.trim()}>
                            {pending ? 'Running…' : 'Run search'}
                        </Button>
                    ) : (
                        <Link
                            to="/signin"
                            className="inline-flex items-center justify-center rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-text-on-primary hover:border-primary-hover hover:bg-primary-hover"
                        >
                            Sign in to run
                        </Link>
                    )}
                    {isFull ? (
                        <p className="text-xs text-text-muted">
                            Snippets rewrite live for /v1 (max {PUBLIC_COUNT_MAX}). Run clamps count to{' '}
                            {CONSOLE_COUNT_MAX}.
                        </p>
                    ) : null}
                </div>
            </form>

            {error ? (
                <div className="mb-5">
                    <Note tone="warn">{error}</Note>
                </div>
            ) : null}

            {result ? (
                <ResultTable
                    result={result}
                    formCountry={form.country}
                    formCurrency={poolCurrency}
                    pageItems={pageItems}
                    pageStart={pageStart}
                    itemCount={itemCount}
                    totalItemPages={totalItemPages}
                    safeItemPage={safeItemPage}
                    setItemPage={setItemPage}
                />
            ) : null}

            <div className="space-y-5">
                <CodeBlock label="curl (API key, not the session)" code={curlSample} />
                {isFull ? <CodeBlock label="JavaScript" code={jsSample} /> : null}
            </div>

            {mode === 'compact' ? (
                <p className="mt-4 text-sm text-text-muted">
                    Full parameter reference, pagination, and field notes on{' '}
                    <Link to="/docs/search" className="text-text underline decoration-border-strong underline-offset-2 hover:decoration-text">
                        /docs/search
                    </Link>
                    .
                </p>
            ) : null}
        </Panel>
    );
}
